import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** The Backoffice's card, kept identical: the two portals are one huisstijl. */
export function Navigatiekaart({
  to,
  icon: Icon,
  titel,
  omschrijving,
  regel,
}: {
  to: string;
  icon: LucideIcon;
  titel: string;
  omschrijving: string;
  /** A node, so a caller can hand in a skeleton line while the line is still loading. */
  regel?: ReactNode;
}) {
  return (
    // `relative`, and not optional: the link below stretches an ::after over the whole card to
    // make it one target, and without a positioned ancestor here that overlay takes its size
    // from the page shell instead - the cards stacked over the content, the last one
    // swallowing every click and showing as permanently hovered.
    <Card className="relative transition-colors hover:border-secondary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon aria-hidden className="size-4 shrink-0" />
          {/* The overlay makes this the whole card's link, so it carries the accessible name. */}
          <Link to={to} className="after:absolute after:inset-0 focus-visible:outline-none">
            {titel}
          </Link>
        </CardTitle>
        <CardDescription>{omschrijving}</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
        {/* A fixed-width slot so a card with no line is the same height as one with. */}
        <span className="min-w-0 flex-1">{regel}</span>
        <ChevronRight aria-hidden className="size-4 shrink-0" />
      </CardContent>
    </Card>
  );
}
